import { FormActionsMui } from '@app/components/form/FormActionsMui';
import { SelectAllOrNoneMui } from '@app/components/form/SelectAllOrNoneMui';
import { UserSelectField } from '@app/components/users/UsersSelectField';
import { createGroup, fetchUsers } from '@app/modules/admin/api';
import { yupResolver } from '@hookform/resolvers/yup';
import { Group, mapPromises, User, UserRole } from '@mi-front/core';
import { useToast } from '@app/core/notifications';
import { AsyncView } from '@mi-front/ui';
import { Alert, Grid, Stack } from '@mui/material';
import { FormContainer, TextFieldElement, useFormContext, useFormState } from 'react-hook-form-mui';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { GroupForSave } from '../../../../../core/types/users';

const groupFormSchema = yup.object().shape({
  name: yup.string().required(),
  users: yup.array(yup.mixed()).default([]),
});

type GroupFormValues = {
  name: string;
  users: User[];
};

export const GroupFormMui = (p: {
  readonly onSuccess: (data: Group) => any;
  readonly cancel: () => any;
}) => {
  const { formatMessage, $t } = useIntl();
  const { notifySuccess } = useToast();

  return (
    <AsyncView
      promiseFn={() =>
        mapPromises({
          availableUsers: fetchUsers({ pageSize: 999 }).then(({ results }) =>
            results.filter((u) => u.role !== UserRole.CLIENT_ADMIN),
          ),
        })
      }
    >
      {(asyncData) => {
        const initialUsers = asyncData.data.availableUsers;

        const FormFields = () => {
          const { setValue, watch } = useFormContext<GroupFormValues>();
          const state = useFormState();
          const selectedUsers = watch('users') as User[];
          const allSelected = selectedUsers.length === initialUsers.length;

          return (
            <Stack spacing={4}>
              <Grid container>
                <Grid item md={6} lg={5}>
                  <TextFieldElement
                    name="name"
                    label={formatMessage({
                      id: 'admin.groups.form.name.label',
                      defaultMessage: 'Nombre',
                    })}
                    placeholder={formatMessage({
                      id: 'admin.groups.form.name.placeholder',
                      defaultMessage: 'Por ejemplo "Usuarios de Emisoras Principales"',
                    })}
                    fullWidth
                    required
                  />
                </Grid>
              </Grid>
              <Grid container>
                <Grid item md={12} lg={10}>
                  <SelectAllOrNoneMui
                    onSelectAll={
                      !allSelected ? () => setValue('users', [...initialUsers]) : undefined
                    }
                    onSelectNone={
                      selectedUsers.length > 0 ? () => setValue('users', []) : undefined
                    }
                  >
                    <UserSelectField
                      name="users"
                      label={formatMessage({
                        id: 'admin.groups.form.users.label',
                        defaultMessage: 'Usuarios asignados',
                      })}
                      users={initialUsers}
                      multiple
                      noOptionsText={formatMessage({
                        id: 'admin.groups.form.users.noMore',
                        defaultMessage: 'No hay más usuarios disponibles',
                      })}
                    />
                  </SelectAllOrNoneMui>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <FormattedMessage
                      id="admin.groups.form.users.help"
                      defaultMessage="Los usuarios pueden pertenecer a sólo un grupo. Los usuarios seleccionados que estén vinculados a otros grupos serán sobreescritos."
                    />
                  </Alert>
                </Grid>
              </Grid>
              <FormActionsMui cancel />
            </Stack>
          );
        };

        return (
          <FormContainer
            defaultValues={{
              name: '',
              users: [],
            }}
            resolver={yupResolver(groupFormSchema)}
            onSuccess={async (data: GroupFormValues) => {
              const dataToSave: GroupForSave = {
                name: data.name,
                usersIds: data.users.map((u: User) => u.id),
              };

              const group = await createGroup(dataToSave);
              notifySuccess(
                $t({
                  id: 'admin.groups.notifications.created',
                  defaultMessage: 'Grupo creado',
                }),
              );
              p.onSuccess(group);
            }}
          >
            <FormFields />
          </FormContainer>
        );
      }}
    </AsyncView>
  );
};
